import { Button, cn, RiArrowLeftLine, RiArrowRightLine, Stack } from 'component-library';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popup, PopupProps } from 'react-map-gl';

import { Url } from '@/components/Url';

import { MediaMapLayerDataItem } from './constants';
import { MediaFilePreview } from './MediaFilePreview';

const POP_UP_WIDTH = 370;

const KNOWN_LABELS = [
  'longitude',
  'latitude',
  'species',
  'common_name',
  'description',
  'mode_of_sight',
  'time_of_sight',
  'credits',
  'url',
] as const;

type KnownLabel = (typeof KNOWN_LABELS)[number];

type MarkerPopupProps = Omit<PopupProps, 'longitude' | 'latitude' | 'children'> & {
  markerItems: MediaMapLayerDataItem[];
  onClose: () => void;
};

export const MarkerPopup = ({ markerItems, onClose, ...delegated }: MarkerPopupProps) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentItem = markerItems[`${currentIndex}`];

  if (!currentItem) return null;

  const { latitude, longitude, value, ...sectionProperties } = currentItem;
  const hasMultipleItems = markerItems.length > 1;

  const sections = Object.entries(sectionProperties)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => {
      const formattedKey = KNOWN_LABELS.includes(k as KnownLabel) ? k : k.replaceAll('_', ' ');

      return [formattedKey, v] as [string, string];
    });

  return (
    <Popup
      anchor='left'
      longitude={parseFloat(longitude)}
      latitude={parseFloat(latitude)}
      closeButton={false}
      maxWidth='none'
      offset={12}
      onClose={onClose}
      {...delegated}
    >
      <Stack
        className='relative items-center animate-in fade-in slide-in-from-left-2'
        style={{ '--popup-content-translate-y': '-0.75rem' } as React.CSSProperties}
      >
        <Stack
          className={`absolute left-6 w-[${POP_UP_WIDTH}px] -translate-y-1/2 rounded-lg max-h-[500px] bg-white-100 p-4 overflow-y-auto`}
          spacing={6}
        >
          {hasMultipleItems && (
            <Stack direction='row' className='items-center justify-between'>
              <Button
                data-testid='marker-popup-nav-previous'
                onClick={() => setCurrentIndex((i) => i - 1)}
                disabled={currentIndex === 0}
                className='p-1'
                size='small'
                variant='text'
              >
                <RiArrowLeftLine />
              </Button>
              <span className='typography-caption text-text-secondary'>
                {t('shared.projects.map.mediaMap.markerPopup.navigation.counter', {
                  current: currentIndex + 1,
                  total: markerItems.length,
                })}
              </span>
              <Button
                data-testid='marker-popup-nav-next'
                onClick={() => setCurrentIndex((i) => i + 1)}
                disabled={currentIndex === markerItems.length - 1}
                className='p-1'
                size='small'
                variant='text'
              >
                <RiArrowRightLine />
              </Button>
            </Stack>
          )}
          <MediaFilePreview fileInfoId={value} />
          <Stack spacing={6} direction='row' className='flex-wrap'>
            {sections.map(([k, v]) => (
              <InfoSection key={k} label={k} info={v} />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Popup>
  );
};

const InfoSection = ({ label, info }: { label: string; info: string }) => {
  const { t } = useTranslation();

  const labelText = KNOWN_LABELS.includes(label as KnownLabel)
    ? t(`shared.projects.map.mediaMap.markerPopup.infoLabel.${label as KnownLabel}`)
    : label;

  const applyItalics = label === 'species';
  const isLink = label === 'url';

  return (
    <Stack spacing={2}>
      <span className='typography-overline text-text-secondary'>{labelText}</span>
      <span className={cn('typography-body1', applyItalics && 'italic')}>{isLink ? <Url url={info} /> : info}</span>
    </Stack>
  );
};
